import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material';
import { Router, Routes } from '@angular/router';
import { dialogRoutes } from '@components/settings/settings-routing.module';
import { IAppStatus } from '@models/menu/app-status';
import { IMenuItem } from '@models/menu/menu-item';
import { IMenuOption, MenuOptions } from '@models/menu/options';
import { IProduct, IProductMenOption, IProductTab, allProductsItem } from '@models/product';
import { IDialogSaveObject } from '@models/settings/dialog-save-object';
import { UserLevel } from '@models/user-level';
import { OpenIDConnectService } from '@services/auth/openid-connect.service';
import { WindowRef } from '@services/window-ref.service';
import { clone } from '@shared/arrays';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from './user.service';

const log = (...args) => {
  // Set to true for debugging
  if (false) {
    console.log(`🍊 %c [Menu] ${args[0]}`, 'font-family: Helvetica; color: orange; font-weight: 600;', ...args.slice(1));
  }
};

@Injectable({ providedIn: 'root' })
export class MenuService {
  private readonly _isOpenDialog$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public lockedProductNames: string[] = [];

  // Dialog Save button
  private readonly saveButtonInitialState: IDialogSaveObject = {
    page: null,
    enabled: false,
    unsavedChanges: false,
    clicked: false,
    valid: false,
    source: 'initialState',
    running: false
  };

  public tabs: IMenuItem[] = [];
  public dialogRef: MatDialogRef<any>;
  public dialogSaveButton$: BehaviorSubject<IDialogSaveObject> = new BehaviorSubject<IDialogSaveObject>(this.saveButtonInitialState);
  public halfSized$ = new BehaviorSubject<boolean>(false);
  public maximized$ = new BehaviorSubject<boolean>(false);
  public selectedOption$: BehaviorSubject<IMenuOption> = new BehaviorSubject<IMenuOption>(MenuOptions.All()[0]);
  public selectedProduct$: BehaviorSubject<IProduct> = new BehaviorSubject<IProduct>(allProductsItem);
  public selectedTab$: BehaviorSubject<IMenuItem> = new BehaviorSubject<IMenuItem>(null);
  public selectingOption$: BehaviorSubject<IMenuOption> = new BehaviorSubject<IMenuOption>(MenuOptions.All()[0]);
  public selectingProduct$: BehaviorSubject<IProduct> = new BehaviorSubject<IProduct>(allProductsItem);
  public reloadHeader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public toBeClosed$ = new BehaviorSubject<string>('');
  public userLevelIds: number[] = [];
  public selectedProductTab$: BehaviorSubject<IProductTab> = new BehaviorSubject<IProductTab>({
    Id: -1,
    Name: 'All Products',
    type: 'tab',
    DateTimeCreated: null,
    DateTimeModified: null,
    Products: [],
    IsPublished: false
  });

  public selectedMenuOption$: BehaviorSubject<IProductMenOption> = new BehaviorSubject<IProductMenOption>(null);
  // 🌟 Navigation
  /**
   * This boolean holds whether the active component can be deactivated or not (main outlet).
   * The component is responsible for updating it.
   */
  // public _canDeactivate = true;

  /**
   * @description Click Handler function triggered when the user clicks the "Save" button
   * on the dialog when is in a Model Settings Page
   */

  public set dialogSaveButton(newStatePiece: IDialogSaveObject) {
    const currentState: IDialogSaveObject = clone({ ...this.dialogSaveButton });
    let newState: IDialogSaveObject = { ...currentState, ...newStatePiece };

    if (newState.page !== currentState.page) {
      newState = { ...this.saveButtonInitialState, ...newState };
    }

    if (newState.page === 'forecast') {
      newState.unsavedChanges = this.productForecastStatus.unsavedChanges || this.marketRateForecastStatus.unsavedChanges;
      newState.valid = this.productForecastStatus.valid && this.marketRateForecastStatus.valid;
      newState.running = this.productForecastStatus.running || this.marketRateForecastStatus.running;
      newState.enabled = newState.unsavedChanges && newState.valid && !newState.running;
    } else {
      newState.enabled = newState.page !== null && newState.unsavedChanges && newState.valid && !newState.running;
    }

    const hasChanged = Object.keys(currentState).some(key => currentState[key] !== newState[key]);
    if (hasChanged) {
      this.dialogSaveButton$.next(newState);
    }
  }

  public get dialogSaveButton(): IDialogSaveObject {
    return this.dialogSaveButton$.value;
  }

  get isOpenDialog$() {
    return this._isOpenDialog$.asObservable();
  }
  get isOpenDialog() {
    return this._isOpenDialog$.getValue();
  }
  set isOpenDialog(value: boolean) {
    if (value !== this.isOpenDialog) {
      if (value && !this.selectedTab) {
        // this.selectTab(this.tabs[0]);
      } else if (!value) {
        this.selectedTab = null;
      }
      this._isOpenDialog$.next(value);
    }
  }

  get selectedTab() {
    return this.selectedTab$.getValue();
  }

  set selectedTab(tab: IMenuItem) {
    this.dialogSaveButton = { ...this.dialogSaveButton, enabled: false, clicked: false };
    this.selectedTab$.next(tab);
  }

  // ⭐ Product
  get selectedProduct() {
    return this.selectedProduct$.getValue();
  }
  set selectedProduct(product: IProduct) {
    if (product.ProductId !== this.selectedProduct.ProductId) {
      this.selectedProduct$.next(product);
    }
  }
  // ⭐ ProductTab
  get selectedProductTab() {
    return this.selectedProductTab$.getValue();
  }
  set selectedProductTab(tab: IProductTab) {
    if (tab) {
    }
    this.selectedProductTab$.next(tab);
  }
  // ⭐ Option
  get selectedOption() {
    return this.selectedOption$.getValue();
  }
  set selectedOption(option: IMenuOption) {
    if (option.label !== this.selectedOption.label) {
      this.selectedOption$.next(option);
    }
  }
  // ⭐ MenuOption
  get selectedMenuOption() {
    return this.selectedMenuOption$.getValue();
  }
  set selectedMenuOption(option: IProductMenOption) {
    this.selectedMenuOption$.next(option);
  }

  // ⭐ Header
  get selectedHeaderVal() {
    return this.reloadHeader$.getValue();
  }
  set selectedHeaderVal(header: boolean) {
    this.reloadHeader$.next(header);
  }

  // Forecasts
  public productForecastStatus: IAppStatus = { unsavedChanges: false, valid: true };
  public marketRateForecastStatus: IAppStatus = { unsavedChanges: false, valid: true };

  //
  // ___________________________________________________________________________________________
  // ___________________________________________________________________________________________
  //

  constructor(
    private readonly router: Router,
    private readonly auth: OpenIDConnectService,
    private readonly userService: UserService,
    private readonly winRef: WindowRef
  ) {
    this.router.events.subscribe(res => {});
    this.userService.getUsers().subscribe(() => {
      this.userLevelIds = this.auth.userLevelList.map(ul => ul.level_id);
      if (this.tabs.length === 0) {
        this.setTabsFromRoutes(dialogRoutes);
      }
    });
  }

  public setTabsFromRoutes(routes: Routes): void {
    this.tabs = routes
      .filter(r => r.path)
      .map(r => ({
        routerLink: r.path,
        outlet: r.outlet,
        label: r.path.replace(/\-/g, ' '),
        canDeactivate: true
      }));
    if (!this.isDepositsAdmin()) {
      this.tabs = this.tabs.filter(t => t.routerLink !== 'global-settings');
    }
  }

  public isDepositsReader(): boolean {
    return this.userLevelIds.includes(UserLevel.DepositsReader) || this.isDepositsUser();
  }
  public isDepositsUser(): boolean {
    return this.userLevelIds.includes(UserLevel.DepositsUser) || this.isDepositsAdmin();
  }
  public isDepositsAdmin(): boolean {
    return this.userLevelIds.includes(UserLevel.DepositsAdministrator);
  }

  //public isBIConsumer(): boolean {
  //  return this.userLevelIds.includes(UserLevel.BiReportViewer) || this.userLevelIds.includes(UserLevel.BiReportCreator);
  //}

  public getMaxUserLevel(): string {
    if (this.isDepositsAdmin()) {
      return 'Deposits Admin';
    }
    if (this.isDepositsUser()) {
      return 'Deposits User';
    }
    if (this.isDepositsReader()) {
      return 'Deposits Reader';
    }
    return '?';
  }

  public setDialogRef(dialogRef: MatDialogRef<any, any>): any {
    this.dialogRef = dialogRef;
  }

  /**
   * Gets or Sets the _canDeactivate value, using pop-up if necessary
   */
  // public async canDeactivate(value: boolean = null): Promise<boolean> {
  //   if (value === null) {
  //     if (this.isOpenDialog) {
  //       return true;
  //       // return this.selectedTabCanDeactivate() || !!(await this.canDeactivatePopup());
  //     } else {
  //       const isSameProduct = this.selectedProduct.ProductId === this.selectingProduct.ProductId;
  //       // Prevent locked products to be selected
  //       const isLockedProduct = this.isLockedProduct(this.selectingProduct);
  //       const ableToDeactivate = this._canDeactivate || !!(await this.canDeactivatePopup(this.selectedOption.label ? true : false));
  //       return isSameProduct || isLockedProduct || ableToDeactivate;
  //     }
  //   }
  //   // else
  //   this._canDeactivate = value;
  //   return this._canDeactivate;
  // }

  public isLockedProduct(product: IProduct): boolean {
    return this.lockedProductNames.includes(product.ProductName);
  }

  public selectProduct(product: IProduct) {
    log('selectProduct', product);
    if (product) {
      this.selectedProduct = product;
    }
  }

  public selectOption(option: IMenuOption) {
    if (option) {
      this.selectedOption = option;
    }
  }

  public emitResize(delay = 0) {
    setTimeout(() => this.winRef.nativeWindow.dispatchEvent(new Event('resize')), delay);
  }

  //
  //   Dialog
  //

  /**
   * @description This will emit the name of the tab that is being closed.
   * Components subscribed to this can act before being closed.
   */
  public prepareToClose() {
    this.toBeClosed$.next(this.selectedTab.label);
  }

  public closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
      this.isOpenDialog = false;
      this.dialogSaveButton = { ...this.saveButtonInitialState, source: 'closeDialog' };
    }
  }

  public toggleMaximized() {
    const currentValue = this.maximized$.getValue();
    this.maximized$.next(!currentValue);
  }

  public toggleHalfSized() {
    const currentValue = this.halfSized$.getValue();
    this.halfSized$.next(!currentValue);
  }

  public setPaneAnimation(hideSplitter = false) {
    // @ts-ignore
    document.querySelectorAll('.k-pane').forEach(pane => (pane.style.transition = 'all 0.3s ease'));
    // @ts-ignore
    document.querySelector('.k-splitbar.k-splitbar-horizontal').style.display = 'flex';

    setTimeout(() => {
      this.emitResize();
    }, 100);

    setTimeout(() => {
      // @ts-ignore
      document.querySelectorAll('.k-pane').forEach(pane => (pane.style.transition = ''));
      if (hideSplitter) {
        // @ts-ignore
        document.querySelector('.k-splitbar.k-splitbar-horizontal').style.display = 'none';
      }
      this.emitResize();
    }, 330);
  }

  /**
   * Navigates to a component
   * if no optionName and no product is provided will navigate to the back test report
   * if no optionName is provided will navigate to that products dashboard
   */
  public navigateToComponent(optionName?: string, product?: IProduct) {
    if (!optionName && !product) {
      const _option = MenuOptions.getOptionByLabel('Backtest Report');
      this.router.navigate([_option.path]);
      return;
    } else if (product && !optionName) {
      this.router.navigate(['dashboard', product.ProductId]);
      return;
    }

    const option = MenuOptions.getOptionByLabel(optionName);

    if (option) {
      if (option.group === 'all') {
        if (this.selectedOption.path !== option.path) {
          this.router.navigate([option.path]);
        }
      } else if (option.group === 'product') {
        if (product && product.ProductId !== this.selectedProduct.ProductId) {
          this.router.navigate([option.path, product.ProductId]);
        } else {
          this.router.navigate([option.path, this.selectedProduct.ProductId]);
        }
      }
    }
  }

  public getHeaderVal(): Observable<any> {
    return this.reloadHeader$.asObservable();
  }
}
